import { useStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const enum DefaultCurrencies {
    CRYPTO = 'BTC',
    FIAT = 'EUR'
}

export interface CurrencyPair {
    currencyPair: string;
    baseCurrency: Currency;
    quoteCurrency: Currency;
    buy: CurrencyLimits;
    sell: CurrencyLimits;
}

export interface Currency {
    name: string;
    code: string;
    decimals: number;
    chain: string | null;
    ticker: string;
}

export interface CurrencyLimits {
    status: 'enabled' | 'disabled';
    min: CurrencyAmount;
    max: CurrencyAmount;
}

export interface CurrencyAmount {
    amount: number;
    currencyCode: string;
}

export const useCurrenciesStore = defineStore('currencies', () => {
    const currencyPairs = ref<CurrencyPair[]>([]);
    const cryptoCurrencies = ref<string[]>([]);
    const fiatCurrencies = ref<string[]>([]);
    const selectedCryptoCurrency = useStorage<Currency>('selected-coin', {
        name: null,
        code: 'BTC',
        decimals: null,
        chain: null,
        ticker: 'BTC'
    });

    const selectedFiatCurrency = ref<Currency>(null);

    function getCurrencyPair(crypto?: string, fiat?: string): CurrencyPair {
        if (!currencyPairs.value || !currencyPairs.value.length) {
            return null;
        }

        const pair = `${crypto?.toUpperCase() || DefaultCurrencies.CRYPTO}-${fiat?.toUpperCase() || DefaultCurrencies.FIAT}`;
        return currencyPairs.value.find((currency) => pair === currency.currencyPair) ?? null;
    }

    function setCurrencyPairs(pairs: CurrencyPair[]): void {
        currencyPairs.value = pairs ?? [];
        if (!currencyPairs.value.length) {
            cryptoCurrencies.value = [];
            fiatCurrencies.value = [];
            return;
        }

        cryptoCurrencies.value = Array.from(new Set(currencyPairs.value.map((currency) => currency.baseCurrency.code)));
        fiatCurrencies.value = Array.from(new Set(currencyPairs.value.map((currency) => currency.quoteCurrency.code)));

        setCryptoCurrency(autoSelectCurrency(selectedCryptoCurrency.value, cryptoCurrencies.value, DefaultCurrencies.CRYPTO));
        setFiatCurrency(autoSelectCurrency(selectedFiatCurrency.value, fiatCurrencies.value, DefaultCurrencies.FIAT));
    }

    function setCurrencyPair(pair: CurrencyPair): void {
        if (!pair) {
            selectedCryptoCurrency.value = null;
            selectedFiatCurrency.value = null;
            return;
        }

        if (!currencyPairs.value.some((currency) => currency.currencyPair === pair.currencyPair)) {
            return;
        }

        selectedCryptoCurrency.value = pair.baseCurrency;
        selectedFiatCurrency.value = pair.quoteCurrency;
    }

    function setCryptoCurrency(code: string): void {
        if (isSelectedCurrency(selectedCryptoCurrency.value, code)) {
            return;
        }

        selectedCryptoCurrency.value = findCurrency(currencyPairs.value, code, 'baseCurrency');
    }

    function setFiatCurrency(code: string): void {
        if (isSelectedCurrency(selectedFiatCurrency.value, code)) {
            return;
        }

        selectedFiatCurrency.value = findCurrency(currencyPairs.value, code, 'quoteCurrency');
    }

    return {
        currencyPairs: computed<CurrencyPair[]>(() => currencyPairs.value),
        cryptoCurrencies: computed<string[]>(() => cryptoCurrencies.value),
        fiatCurrencies: computed<string[]>(() => fiatCurrencies.value),
        selectedCryptoCurrency: computed<Currency>(() => selectedCryptoCurrency.value),
        selectedFiatCurrency: computed<Currency>(() => selectedFiatCurrency.value),
        selectedCurrencyPair: computed<CurrencyPair>(() =>
            selectedCryptoCurrency.value && selectedFiatCurrency.value
                ? currencyPairs.value.find((pair) => pair.currencyPair === `${selectedCryptoCurrency.value.code}-${selectedFiatCurrency.value.code}`)
                : null
        ),
        disabledCurrencyPairs: computed<CurrencyPair[]>(() =>
            currencyPairs.value.filter((currency) => currency.buy.status === 'disabled' || currency.sell.status === 'disabled')
        ),
        getCurrencyPair,
        setCurrencyPairs,
        setCurrencyPair,
        setCryptoCurrency,
        setFiatCurrency
    };
});

function findCurrency(pairs: CurrencyPair[], code: string, type: 'quoteCurrency' | 'baseCurrency'): Currency {
    if (!code) {
        return null;
    }

    const value = code.toUpperCase();
    const found = pairs.find((pair) => pair[type].code === value);

    if (!found) {
        // We'll use a placeholder currency which is replaced once the currency pairs are retrieved
        return {
            name: code,
            code: code,
            decimals: 0,
            chain: null,
            ticker: null
        };
    }

    return found[type];
}

function isSelectedCurrency(currency: Currency, code: string): boolean {
    return !!currency && currency.code === code && currency.name !== code;
}

function autoSelectCurrency(currency: Currency, allowed: string[], fallback: DefaultCurrencies): string {
    if (currency && allowed.includes(currency.code)) {
        return currency.code;
    }

    if (allowed.includes(fallback)) {
        return fallback;
    }

    return allowed[0];
}
